import { FormProps } from 'antd/es/form';
import { ModalProps } from 'antd/es/modal';
import { ModalStyles } from 'rc-dialog/lib/IDialogPropTypes';
import type { Callbacks } from 'rc-field-form/lib/interface';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Typography } from 'components';
import styles from './styles.module.scss';

interface CustomModalProps extends ModalProps {
  form: FormProps['form'];
  children: React.ReactNode;
  onSubmit: FormProps['onFinish'];
  onCancel: () => void;
  okText?: string;
  disableOkBtn?: boolean;
  isLoading?: boolean;
  cancelText?: string;
  footerButtons?: React.ReactNode;
  initialValues?: Record<string, any>;
  onValuesChange?: Callbacks['onValuesChange'];
  autoComplete?: string;
}

const modalStyles: ModalStyles = {
  body: {
    maxHeight: '70vh',
    overflowY: 'auto',
    margin: '-20px -24px',
  },
  header: {
    height: 64,
  },
};

export const FormModal: React.FC<CustomModalProps> = (props) => {
  const {
    form,
    children,
    onSubmit,
    onCancel,
    okText = 'Submit',
    cancelText = 'Cancel',
    disableOkBtn = true,
    isLoading = false,
    footerButtons,
    initialValues,
    onValuesChange: onFormValuesChange,
    autoComplete,
    ...modalProps
  } = props;
  const modalBodyRef = useRef<HTMLDivElement>(null); // Ref to modal body content
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const resetScroll = useCallback(() => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0;
    }
  }, []);

  const handelCancel = useCallback(() => {
    onCancel();
    resetScroll();
  }, [onCancel, resetScroll]);

  const onValuesChange = useCallback(
    (_: any, values: any) => {
      setDisableSaveBtn(false);
      onFormValuesChange && onFormValuesChange(_, values);
    },
    [onFormValuesChange]
  );

  useEffect(() => {
    if (!modalProps.open) {
      setDisableSaveBtn(true); // Always disable when closing
    } else {
      setDisableSaveBtn(disableOkBtn); // Follow parent prop on open
    }
  }, [modalProps.open, disableOkBtn]);

  return (
    <Modal
      closable={false}
      width={680}
      {...modalProps}
      title={modalProps?.title ? <Typography className={styles.form_modal_title}>{modalProps?.title}</Typography> : ''}
      onCancel={handelCancel}
      styles={{ ...modalStyles }}
      bodyProps={{ ref: modalBodyRef }}
      centered
      footer={
        <Row gutter={[0, 20]}>
          <Col span={24}>
            <Row wrap={false} className="gap-8" style={{ margin: '24px 0 12px' }}>
              {footerButtons ? (
                footerButtons
              ) : (
                <>
                  <Button block size="large" onClick={handelCancel}>
                    {cancelText}
                  </Button>
                  <Button
                    block
                    size="large"
                    disabled={disableSaveBtn}
                    type="primary"
                    loading={isLoading}
                    onClick={() => {
                      form?.submit();
                      resetScroll();
                    }}
                    {...modalProps.okButtonProps}
                  >
                    {okText}
                  </Button>
                </>
              )}
            </Row>
          </Col>
        </Row>
      }
    >
      <Form
        onValuesChange={onValuesChange}
        initialValues={initialValues}
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark={false}
        autoComplete={autoComplete}
      >
        <div className={styles.modal_body}>{children}</div>
      </Form>
    </Modal>
  );
};
